import CardMedia from 'components/CardMedia';
import React from 'react';
import {
  Alert,
  AlertTitle,
  Card,
  Divider,
  Rating,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { priceTransformer } from 'utils';
import useIsDesktop from 'hooks/useIsDesktop';
import TagIcon from '@mui/icons-material/SellOutlined';
import SnoozeOutlinedIcon from '@mui/icons-material/SnoozeOutlined';
import Skeleton from 'components/Skeleton';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined';
import PickupIcon from '@mui/icons-material/DirectionsWalkRounded';
import { getMerchantImage } from 'utils/merchant';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ScheduleButton from './ScheduleButton';

function Header({
  merchant,
  schedulingOpen,
  onSchedulingOpenChange,
  loading,
  ...props
}) {
  const isDesktop = useIsDesktop();

  function renderDeliveryPickup() {
    return (
      <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={1}>
        {merchant?.has_delivery && (
          <Tooltip title="Delivery" arrow>
            <Stack direction="row" alignItems="center">
              <Typography color="text.secondary">
                <DeliveryDiningOutlinedIcon />
                {Boolean(merchant.distance) && ` ${merchant.distance} mi`}
                {Boolean(merchant.distance) && Boolean(merchant.delivery_fee) && ' · '}
                {Boolean(merchant.delivery_fee) &&
                    priceTransformer(merchant.delivery_fee)}
              </Typography>
            </Stack>
          </Tooltip>

        )}
        {merchant?.has_pickup && (
          <Tooltip title="Pickup" arrow>
            <Typography color="text.secondary">
              <PickupIcon />
              {Boolean(merchant?.distance) && ` ${merchant.distance} mi`}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    );
  }

  function renderAlert() {
    if (merchant.takeout) {
      return null;
    }
    if (merchant.has_scheduling_order) {
      return (
        <Alert severity="success" icon={<SnoozeOutlinedIcon />}>
          <AlertTitle>Only schedule ordering is available.</AlertTitle>
          On-demand ordering is currently not available. Please schedule your order for the next available time.
        </Alert>
      );
    }
    return (
      <Alert severity="error" icon={<HighlightOffIcon />}>
        <AlertTitle>Restaurant is closed.</AlertTitle>
        On-demand ordering is currently not available.
      </Alert>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        p: {
          xs: 0,
          md: 4,
        },
        pb: { xs: 2 },
        borderRadius: {
          xs: 0,
          lg: 2,
        },
      }}
      {...props}
    >
      <CardMedia
        sx={{
          width: '100%',
          height: {
            xs: 200,
            md: 220,
            lg: 250,
          },
          position: 'relative',
          borderRadius: 0,
        }}
        loading={loading}
        image={getMerchantImage(merchant, 'md') || merchant.logo}
        alt={merchant?.name}
      >
        <CardMedia
          alt={merchant.name}
          loading={loading}
          image={merchant?.logo}
          sx={{
            width: 64,
            height: 64,
            position: 'absolute',
            backgroundColor: 'background.paper',
            border: '2px solid #fff',
            bottom: 0,
            left: 0,
            m: 3,
            borderRadius: '50%',
          }}
        />
      </CardMedia>
      <Stack
        spacing={1}
        mt={1}
        px={{
          xs: 2,
          md: 0,
        }}
      >
        {renderAlert() }
        {
          isDesktop && (
            <Typography
              variant="h4"
              component="h1"
            >
              {merchant.name}
            </Typography>
          )
        }

        <Stack direction="row" flexWrap="wrap" divider={<Divider orientation="vertical" flexItem />} spacing={2}>
          <Typography color="text.secondary" variant="body2">
            <PlaceOutlinedIcon />
            {' '}
            {merchant.address}
          </Typography>
          {isDesktop && renderDeliveryPickup()}
        </Stack>
        {!isDesktop && renderDeliveryPickup()}

        <Stack direction="row" spacing={2} flexWrap="wrap">
          {merchant.rating > 0 && (
            <Stack direction="row" spacing={1}>
              <Typography fontWeight="fontWeightBold">{merchant.rating}</Typography>
              <Rating readOnly value={merchant.rating} />
            </Stack>
          )}
          {
            merchant.tags?.length > 0 && (
              <Typography color="text.secondary">
                <TagIcon sx={{ mr: 1 }} />
                {merchant.tags.map((t) => t.title).join(' · ')}
              </Typography>
            )
          }

        </Stack>
        {
          merchant.has_scheduling_order && (
            <ScheduleButton
              pt={1}
              merchant={merchant}
              open={schedulingOpen}
              onSchedulingOpenChange={onSchedulingOpenChange}
            />
          )
        }

      </Stack>
    </Card>
  );
}

function HeaderSkeleton() {
  return (
    <Skeleton>
      <Header
        merchant={Skeleton.props({
          has_delivery: true,
          distance: ['string', { length: 2 }],
          delivery_fee: 'price',
          has_pickup: true,
          name: ['string', { length: 15 }],
          address: ['string', { length: 40 }],
          tags: ['array', {
            length: 3,
            value: {
              title: 'string',
            },
          }],
          takeout: true,
        })}
      />
    </Skeleton>
  );
}

export default Skeleton.withSkeleton(Header, HeaderSkeleton);
